/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./CreateGoal.css";

import PropTypes from "prop-types";
import { Editor, RichUtils } from "draft-js";

// Block types live in a dropdown rather than a row of chips, so the whole
// toolbar fits one line at any column width. Headings 4-6 are kept even though
// they're rarely useful in a description: dropping them would make existing
// content that uses them unrepresentable in the editor.
const BLOCK_OPTIONS = [
  { label: "Paragraph", style: "unstyled" },
  { label: "Heading 1", style: "header-one" },
  { label: "Heading 2", style: "header-two" },
  { label: "Heading 3", style: "header-three" },
  { label: "Heading 4", style: "header-four" },
  { label: "Heading 5", style: "header-five" },
  { label: "Heading 6", style: "header-six" },
  { label: "Quote", style: "blockquote" },
  { label: "Code", style: "code-block" },
];

const INLINE_STYLES = [
  { label: "B", style: "BOLD", title: "Bold", className: "is-bold" },
  { label: "I", style: "ITALIC", title: "Italic", className: "is-italic" },
  { label: "U", style: "UNDERLINE", title: "Underline", className: "is-underline" },
  { label: "‹›", style: "CODE", title: "Monospace", className: "is-mono" },
];

const LIST_TYPES = [
  {
    style: "unordered-list-item",
    title: "Bulleted list",
    path: "M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01",
  },
  {
    style: "ordered-list-item",
    title: "Numbered list",
    path: "M10 6h10M10 12h10M10 18h10M4 5l1.5-.8V9M4 18h3l-3-3.2 1.4-1.3",
  },
];

const ToolbarIcon = (props) => (
  <svg viewBox="0 0 24 24" className="RichEditor-icon" aria-hidden="true">
    <path d={props.path} />
  </svg>
);

ToolbarIcon.propTypes = { path: PropTypes.string };

class TextBox extends Component {
  render() {
    const descriptionContent = this.props.descriptionContent;
    const contentState = descriptionContent.getCurrentContent();
    const selection = descriptionContent.getSelection();
    const blockType = contentState
      .getBlockForKey(selection.getStartKey())
      .getType();
    const currentStyle = descriptionContent.getCurrentInlineStyle();

    const setBlockType = (nextType) => {
      // toggleBlockType reverts to "unstyled" when handed the current type,
      // which is exactly what picking Paragraph should do.
      this.props.handleDescription(
        RichUtils.toggleBlockType(descriptionContent, nextType),
      );
    };

    const toggleInlineStyle = (inlineStyle) => {
      this.props.handleDescription(
        RichUtils.toggleInlineStyle(descriptionContent, inlineStyle),
      );
    };

    const onTab = (e) => {
      const maxDepth = 4;
      this.props.handleDescription(
        RichUtils.onTab(e, descriptionContent, maxDepth),
      );
    };

    // Draft leaves the placeholder showing when the block is empty but has a
    // block type applied (e.g. straight after picking Heading 1), where it sits
    // on top of the caret. Hide it in that case.
    const hidePlaceholder =
      !contentState.hasText() &&
      contentState.getBlockMap().first().getType() !== "unstyled";

    return (
      <div className="CreateGoal-textBox">
        <div
          className={
            "CreateGoal-editor" +
            (hidePlaceholder ? " RichEditor-hidePlaceholder" : "")
          }
          data-testid="creategoal-description-editor"
        >
          <div className="RichEditor-toolbar">
            <select
              className="RichEditor-blockSelect"
              value={
                BLOCK_OPTIONS.some((o) => o.style === blockType)
                  ? blockType
                  : "unstyled"
              }
              onChange={(e) => setBlockType(e.target.value)}
            >
              {BLOCK_OPTIONS.map((option) => (
                <option key={option.style} value={option.style}>
                  {option.label}
                </option>
              ))}
            </select>

            <span className="RichEditor-divider" />

            <div className="RichEditor-group">
              {INLINE_STYLES.map((type) => (
                <StyleButton
                  key={type.style}
                  active={currentStyle.has(type.style)}
                  label={type.label}
                  title={type.title}
                  extraClass={type.className}
                  onToggle={toggleInlineStyle}
                  style={type.style}
                />
              ))}
            </div>

            <span className="RichEditor-divider" />

            <div className="RichEditor-group">
              {LIST_TYPES.map((type) => (
                <StyleButton
                  key={type.style}
                  active={type.style === blockType}
                  title={type.title}
                  icon={<ToolbarIcon path={type.path} />}
                  onToggle={setBlockType}
                  style={type.style}
                />
              ))}
            </div>
          </div>

          <Editor
            editorState={descriptionContent}
            onChange={this.props.handleDescription}
            blockStyleFn={getBlockStyle}
            customStyleMap={styleMap}
            onTab={onTab}
            placeholder={this.props.placeholder}
            spellCheck={true}
          />
        </div>
      </div>
    );
  }
}

const styleMap = {
  CODE: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
};

function getBlockStyle(block) {
  switch (block.getType()) {
    case "blockquote":
      return "RichEditor-blockquote";
    default:
      return null;
  }
}

class StyleButton extends React.Component {
  constructor() {
    super();
    this.onToggle = (e) => {
      e.preventDefault();
      this.props.onToggle(this.props.style);
    };
  }

  render() {
    let className = "RichEditor-styleButton";
    if (this.props.extraClass) {
      className += " " + this.props.extraClass;
    }
    if (this.props.active) {
      className += " RichEditor-activeButton";
    }

    return (
      <button
        type="button"
        className={className}
        title={this.props.title}
        aria-label={this.props.title}
        aria-pressed={!!this.props.active}
        onMouseDown={this.onToggle}
      >
        {this.props.icon || this.props.label}
      </button>
    );
  }
}

StyleButton.propTypes = {
  onToggle: PropTypes.func,
  style: PropTypes.string,
  active: PropTypes.bool,
  label: PropTypes.string,
  title: PropTypes.string,
  extraClass: PropTypes.string,
  icon: PropTypes.node,
};

TextBox.propTypes = {
  textObj: PropTypes.object,
  handleDescription: PropTypes.func,
  descriptionContent: PropTypes.object,
  placeholder: PropTypes.string,
};

export default TextBox;
